import { useCallback, useEffect, useState } from "react";
import { isLang, pickLang } from "@b00k/domain";
import type { Lang } from "@b00k/domain";

export type { Lang };

const KEY = "b00k-lang";

/**
 * Wybór języka wspólny dla wszystkich aplikacji b00k (utrwalony w localStorage).
 *
 * Przy pierwszej wizycie sięgamy po preferencje przeglądarki, zamiast zaczynać
 * od polskiego: produkt mówi dziewięcioma językami i pokazywanie Hiszpanowi
 * polskiego interfejsu tylko dlatego, że jeszcze nic nie kliknął, byłoby
 * marnowaniem tej pracy. Raz dokonany wybór ma pierwszeństwo nad przeglądarką.
 */
export function useLang(initial?: Lang): [Lang, (l: Lang) => void] {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return initial ?? "pl";
    const zapisany = window.localStorage.getItem(KEY);
    if (isLang(zapisany)) return zapisany;
    return initial ?? pickLang(window.navigator.languages ?? [window.navigator.language]);
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      window.localStorage.setItem(KEY, l);
      // `storage` nie dociera do okna, które zapisało — a inne części aplikacji
      // (formatowanie kwot) muszą się dowiedzieć o zmianie bez przeładowania.
      window.dispatchEvent(new Event("b00k-lang"));
    } catch {
      /* prywatny tryb przeglądarki — wybór działa tylko w tej sesji */
    }
  }, []);

  return [lang, setLang];
}
